export type SupportingVocabularyWord = {
  bangla: string;
  phonetic: string;
  meaning: string;
};

export type SupportingVocabularyPack = {
  lessonId: number;
  category: string;
  words: readonly SupportingVocabularyWord[];
};

type VocabularyRow = readonly [
  category: string,
  firstBangla: string,
  firstPhonetic: string,
  firstMeaning: string,
  secondBangla: string,
  secondPhonetic: string,
  secondMeaning: string
];

/**
 * Two supporting words per lesson. The words deliberately sit just outside the
 * lesson's three anchor chunks: they make the scene more understandable without
 * turning the spoken course into a word-list course. Forms favor everyday Dhaka
 * family speech, including ordinary Banglish where that is the natural register.
 */
const vocabularyRows: readonly VocabularyRow[] = [
  // Section 1 — At Home
  ["conversation building blocks", "ভালো", "bhalo", "well or good", "অল্প", "olpo", "a little"],
  ["feelings", "খুশি", "khushi", "happy", "মন খারাপ", "mon kharap", "upset"],
  ["speech", "শব্দ", "shobdo", "word", "কথা", "kotha", "speech or remark"],
  ["sequence", "শুরু", "shuru", "beginning", "শেষ", "shesh", "ending"],
  ["understanding", "মানে", "mane", "meaning", "ভাষা", "bhasha", "language"],
  ["frequency", "সবসময়", "shobshomoy", "always", "কখনো না", "kokhono na", "never"],
  ["drinks", "কাপ", "kap", "cup", "গ্লাস", "glass", "glass"],
  ["taste", "মিষ্টি", "mishti", "sweet", "নোনতা", "nonta", "salty"],
  ["how you feel", "আরাম", "aram", "rest or comfort", "এনার্জি", "energy", "energy"],
  ["living room", "টেবিল", "table", "table", "সোফা", "sofa", "sofa"],
  ["small essentials", "ফোন", "phone", "phone", "চাবি", "chabi", "key"],
  ["extended family", "কাজিন", "cousin", "cousin", "আত্মীয়", "attiyo", "relative"],
  ["family order", "সবার বড়", "shobar boro", "eldest", "সবার ছোট", "shobar chhoto", "youngest"],
  ["rooms", "বেডরুম", "bedroom", "bedroom", "বাথরুম", "bathroom", "bathroom"],
  ["timing", "সকাল সকাল", "shokal shokal", "early", "দেরি", "deri", "late"],
  ["the week", "কাজের দিন", "kajer din", "weekday", "ছুটির দিন", "chhutir din", "day off"],
  ["location", "বাইরে", "baire", "outside", "ভিতরে", "bhitore", "inside"],
  ["movement", "দাঁড়ান", "daran", "stand", "থামুন", "thamun", "stop"],
  ["actions", "খুলুন", "khulun", "open", "বন্ধ করুন", "bondho korun", "close"],
  ["at the table", "প্লেট", "plate", "plate", "চামচ", "chamoch", "spoon"],
  ["little accidents", "আওয়াজ", "awaj", "noise", "ভাঙা", "bhanga", "broken"],
  ["certainty", "অবশ্যই", "oboshshoi", "certainly", "হতে পারে", "hote pare", "possibly"],
  ["rainy weather", "ছাতা", "chhata", "umbrella", "বাতাস", "batash", "wind"],
  ["clothes", "প্যান্ট", "pant", "pants", "টুপি", "tupi", "hat"],
  ["colors", "লাল", "lal", "red", "সবুজ", "shobuj", "green"],
  ["leaving warmly", "বাসা", "basha", "home", "সাবধানে", "shabdhane", "carefully"],
  ["kitchen places", "চুলা", "chula", "stove", "ফ্রিজ", "fridge", "fridge"],
  ["checking meaning", "ঠিক", "thik", "correct", "ঠিক না", "thik na", "wrong"],
  ["breakfast", "নাশতা", "nashta", "breakfast", "ডিম", "dim", "egg"],
  ["hospitality", "স্বাগতম", "shagotom", "welcome", "আরামে", "arame", "comfortably"],

  // Section 2 — Family Life in New York
  ["making plans", "দেখা", "dekha", "visit or meeting", "ফ্রি", "free", "free or available"],
  ["time order", "আগে", "age", "before", "পরে", "pore", "after"],
  ["getting around", "স্টেশন", "station", "station", "ট্রাফিক", "traffic", "traffic"],
  ["directions", "পাশ", "pash", "side", "কোণা", "kona", "corner"],
  ["grocery staples", "চাল", "chal", "rice", "ডাল", "dal", "lentils"],
  ["prices", "সস্তা", "shosta", "cheap", "দামি", "dami", "expensive"],
  ["paying", "ঠিকানা", "thikana", "address", "ভাংতি", "bhangti", "change money"],
  ["restaurant", "মেনু", "menu", "menu", "বিল", "bill", "bill"],
  ["flavor", "ঝাল", "jhal", "spicy", "টক", "tok", "sour"],
  ["cooking tools", "ছুরি", "chhuri", "knife", "হাঁড়ি", "hari", "cooking pot"],
  ["recent time", "গতকাল রাতে", "gotokal rate", "last night", "আজ সকালে", "aj shokale", "this morning"],
  ["daily life", "বন্ধু", "bondhu", "friend", "অফিস", "office", "office"],
  ["future time", "ছুটি", "chhuti", "holiday or time off", "আগামী সপ্তাহ", "agami shoptaho", "next week"],
  ["phone trouble", "সিগন্যাল", "signal", "signal", "ব্যাটারি", "battery", "battery"],
  ["confirming plans", "খালি", "khali", "available", "কনফার্ম", "confirm", "confirmed"],
  ["work status", "ব্যস্ত", "byasto", "busy", "শেষ হয়েছে", "shesh hoyeche", "finished"],
  ["common illness", "জ্বর", "jor", "fever", "কাশি", "kashi", "cough"],
  ["medicine", "ব্যথা", "byatha", "pain", "ট্যাবলেট", "tablet", "tablet"],
  ["changed plans", "দেরি হচ্ছে", "deri hochchhe", "delayed", "ক্যানসেল", "cancel", "cancel"],
  ["inviting people", "মেহমান", "mehoman", "guest", "দাওয়াত", "dawat", "invitation"],
  ["possibility", "সম্ভব", "shombhob", "possible", "অসম্ভব", "oshombhob", "impossible"],
  ["explaining", "কারণ", "karon", "reason", "সমস্যা", "shomoshsha", "problem"],
  ["distance", "কাছে", "kache", "near", "দূরে", "dure", "far"],
  ["deciding together", "রাজি", "raji", "agree", "আইডিয়া", "idea", "idea"],
  ["personality", "চুপচাপ", "chupchap", "quiet", "মজার", "mojar", "funny"],
  ["celebrations", "জন্মদিন", "jonmodin", "birthday", "গিফট", "gift", "gift"],
  ["pharmacy errand", "ফার্মেসি", "pharmacy", "pharmacy", "রসিদ", "roshid", "receipt"],
  ["children's day", "খেলার মাঠ", "khelar math", "playground", "হোমওয়ার্ক", "homework", "homework"],
  ["conversation", "প্রশ্ন", "proshno", "question", "উত্তর", "uttor", "answer"],
  ["being prepared", "রেডি", "ready", "ready", "একসাথে", "ekshathe", "together"],

  // Section 3 — Real Family Conversation
  ["speech style", "তাড়াতাড়ি", "taratari", "quickly", "আস্তে", "aste", "softly or slowly"],
  ["pronunciation", "ধ্বনি", "dhoni", "sound", "সিলেবল", "syllable", "syllable"],
  ["work calendar", "মিটিং", "meeting", "meeting", "শিফট", "shift", "work shift"],
  ["asking someone", "রিকোয়েস্ট", "request", "request", "উপকার", "upokar", "favor"],
  ["tone", "ঠাট্টা", "thatta", "joke", "সিরিয়াস", "serious", "serious"],
  ["strong feelings", "চিন্তিত", "chintito", "worried", "রাগ", "rag", "anger"],
  ["group talk", "পালা", "pala", "turn", "সুযোগ", "shujog", "chance"],
  ["family stories", "মানুষ", "manush", "person", "গল্প", "golpo", "story"],
  ["informal speech", "স্ল্যাং", "slang", "slang", "নমুনা", "nomuna", "sample"],
  ["serving food", "বাটি", "bati", "bowl", "ভাগ", "bhag", "portion"],
  ["getting better", "পরামর্শ", "poramorsho", "advice", "সেরে ওঠা", "shere otha", "recovery"],
  ["decisions", "পছন্দ", "pochhondo", "choice", "অন্য উপায়", "onno upay", "alternative"],
  ["telling a story", "প্রথমে", "prothome", "first", "শেষে", "sheshe", "finally"],
  ["inner feelings", "আশা", "asha", "hope", "ভয়", "bhoy", "fear"],
  ["repairing things", "মেরামত", "meramot", "fix", "চলছে", "cholchhe", "working"],
  ["making amends", "ভুল", "bhul", "mistake", "মাফ", "maf", "forgive"],
  ["family culture", "রীতি", "riti", "custom", "দোয়া", "doa", "prayer"],
  ["the past", "ছোটবেলা", "chhotobela", "childhood", "গ্রাম", "gram", "village"],
  ["remembering", "ছবি", "chhobi", "photo", "স্মৃতি", "smriti", "memory"],
  ["family updates", "খবর", "khobor", "news", "ঘোষণা", "ghoshona", "announcement"],
  ["important information", "রিপোর্ট", "report", "report", "ফলাফল", "folafol", "result"],
  ["money and work", "বেতন", "beton", "salary", "খরচ", "khoroch", "expense"],
  ["news stories", "শিরোনাম", "shironam", "headline", "ঘটনা", "ghotona", "incident"],
  ["social reactions", "হাসি", "hashi", "laugh", "লজ্জা", "lojja", "embarrassed"],
  ["what a speaker means", "জোর", "jor", "emphasis", "সন্দেহ", "shondeho", "doubt"],
  ["organizing meaning", "মূল কথা", "mul kotha", "main point", "বিস্তারিত", "bistarito", "detail"],
  ["following talk", "প্রসঙ্গ", "proshongo", "context", "বিষয়", "bishoy", "topic"],
  ["phone conversation", "গলা", "gola", "voice", "কল", "call", "call"],
  ["busy environments", "ভিড়", "bhir", "crowded", "পেছনের আওয়াজ", "pechhoner awaj", "background noise"],
  ["clarifying", "বুঝতে পারা", "bujhte para", "understand", "ব্যাখ্যা করা", "byakkha kora", "explain"],

  // Section 4 — Belonging
  ["travel arrival", "এয়ারপোর্ট", "airport", "airport", "লাগেজ", "luggage", "luggage"],
  ["staying over", "বালিশ", "balish", "pillow", "তোয়ালে", "towale", "towel"],
  ["with the nieces", "খেলা", "khela", "game", "খেলনা", "khelna", "toy"],
  ["school life", "শিক্ষক", "shikkhok", "teacher", "ক্লাস", "class", "class"],
  ["personal interests", "প্রিয়", "priyo", "favorite", "শখ", "shokh", "hobby"],
  ["character", "সাহসী", "shahoshi", "brave", "নরম", "norom", "gentle"],
  ["family teasing", "খুনসুটি", "khunshuti", "banter", "প্রতিক্রিয়া", "protikriya", "reaction"],
  ["car ride", "সিট", "seat", "seat", "ড্রাইভার", "driver", "driver"],
  ["restaurant planning", "রিজার্ভেশন", "reservation", "reservation", "কাস্টমার", "customer", "customer"],
  ["train travel", "প্ল্যাটফর্ম", "platform", "platform", "পৌঁছানো", "pouchhano", "arrival"],
  ["shopping choices", "বদলি", "bodli", "substitute", "ব্র্যান্ড", "brand", "brand"],
  ["cooking ingredients", "পেঁয়াজ", "peyaj", "onion", "মসলা", "moshla", "spice"],
  ["following a recipe", "রেসিপি", "recipe", "recipe", "ধাপ", "dhap", "step"],
  ["the senses", "গন্ধ", "gondho", "smell", "স্বাদ", "shad", "taste"],
  ["Eid at home", "চাঁদ", "chand", "moon", "সাজসজ্জা", "shajshojja", "decorations"],
  ["a wedding", "কনে", "kone", "bride", "বর", "bor", "groom"],
  ["sharing media", "রেকর্ডিং", "recording", "recording", "স্ক্রিন", "screen", "screen"],
  ["health instructions", "ডোজ", "dose", "dose", "প্রেসক্রিপশন", "prescription", "prescription"],
  ["school progress", "গ্রেড", "grade", "grade", "প্রজেক্ট", "project", "project"],
  ["the neighborhood", "প্রতিবেশী", "protibeshi", "neighbor", "ব্লক", "block", "city block"],
  ["conflict", "অন্যায়", "onnay", "unfair", "বিরক্ত", "birokto", "frustrated"],
  ["relationships", "সমর্থন", "shomorthon", "support", "ধৈর্য", "dhoirjo", "patience"],
  ["family values", "অভ্যাস", "obhyash", "habit", "সম্মান", "shomman", "respect"],
  ["New York pressures", "ভাড়া", "bhara", "rent", "যাতায়াত", "jatayat", "commute"],
  ["international travel", "পাসপোর্ট", "passport", "passport", "টিকিট", "ticket", "ticket"],
  ["sending things", "প্যাকেট", "packet", "package", "ওজন", "ojon", "weight"],
  ["making room", "কম্বল", "kombol", "blanket", "জায়গা", "jayga", "space"],
  ["working it out", "মতামত", "motamot", "opinion", "মাঝামাঝি", "majhamajhi", "compromise"],
  ["repairing trust", "ভরসা", "bhorosha", "trust", "দুঃখিত", "dukkhito", "sorry"],
  ["belonging", "উদযাপন", "udjapon", "celebrate", "কৃতজ্ঞ", "kritoggo", "grateful"],

  // Section 5 — Independent Family Bangla
  ["opening a long answer", "ইদানীং", "idaning", "lately", "সবকিছু", "shobkichhu", "everything"],
  ["connecting ideas", "যাই হোক", "jai hok", "anyway", "এদিকে", "edike", "meanwhile"],
  ["precision", "ঠিক ঠিক", "thik thik", "exact", "আনুমানিক", "anumanik", "approximately"],
  ["making a point", "সারকথা", "sharkotha", "summary", "সাজেশন", "suggestion", "suggestion"],
  ["comparison", "একই রকম", "ekoi rokom", "similar", "আলাদা", "alada", "different"],
  ["cause and effect", "প্রক্রিয়া", "prokriya", "process", "পরিণতি", "porinoti", "outcome"],
  ["subway problems", "ট্রান্সফার", "transfer", "transfer", "এক্সিট", "exit", "exit"],
  ["customer service", "রিফান্ড", "refund", "refund", "ম্যানেজার", "manager", "manager"],
  ["urgent care", "জরুরি", "joruri", "urgent", "খালি সময়", "khali shomoy", "opening"],
  ["emergency", "অ্যাম্বুলেন্স", "ambulance", "ambulance", "জ্ঞান আছে", "gyan achhe", "conscious"],
  ["medical follow-through", "চিকিৎসা", "chikitsha", "treatment", "ফলো-আপ", "follow-up", "follow-up"],
  ["personal finance", "বাজেট", "budget", "budget", "ধার", "dhar", "debt"],
  ["work decisions", "লক্ষ্য", "lokkho", "goal", "অপরচুনিটি", "opportunity", "opportunity"],
  ["education and work", "কলেজ", "college", "college", "ক্যারিয়ার", "career", "career"],
  ["identity", "ঐতিহ্য", "oitijjo", "tradition", "পরিবর্তন", "poriborton", "change"],
  ["story timing", "হঠাৎ", "hothat", "suddenly", "শেষমেশ", "sheshmesh", "eventually"],
  ["figurative speech", "বাগধারা", "bagdhara", "idiom", "আক্ষরিক", "akkhorik", "literal"],
  ["healthy boundaries", "সীমা", "shima", "boundary", "অনুমতি", "onumoti", "permission"],
  ["probability", "সম্ভবত", "shombhoboto", "likely", "নিশ্চিত", "nishchito", "certain"],
  ["hard conversations", "শান্ত", "shanto", "calm", "মন দিয়ে শোনা", "mon diye shona", "listen closely"],
  ["voice messages", "ভয়েস নোট", "voice note", "voice note", "ইন্টারফিয়ারেন্স", "interference", "interference"],
  ["tracking speakers", "বক্তা", "bokta", "speaker", "রেফারেন্স", "reference", "reference"],
  ["hearing at a distance", "দূরত্ব", "durotto", "distance", "আশেপাশে", "ashepashe", "nearby"],
  ["regional speech", "অ্যাকসেন্ট", "accent", "accent", "এলাকা", "elaka", "region"],
  ["describing clearly", "বর্ণনা করা", "bornona kora", "describe", "ধরন", "dhoron", "category"],
  ["natural conversation", "আলাপ", "alap", "conversation", "নীরবতা", "nirobota", "silence"],
  ["after a gathering", "আড্ডা", "adda", "gathering or chat", "বেঁচে যাওয়া খাবার", "beche jawa khabar", "leftovers"],
  ["learning from life", "অভিজ্ঞতা", "obhiggota", "experience", "শিক্ষা", "shikkha", "lesson learned"],
  ["difficult listening", "গতি", "goti", "pace", "বিড়বিড়", "birbir", "mumbling"],
  ["independence", "আত্মবিশ্বাসী", "attobishshashi", "confident", "গর্বিত", "gorbito", "proud"]
];

if (vocabularyRows.length !== 150) {
  throw new Error(
    `The Shuni vocabulary spine must have 150 lesson pairs; found ${vocabularyRows.length}.`
  );
}

export const supportingVocabularyPacks: readonly SupportingVocabularyPack[] =
  vocabularyRows.map((row, index) => ({
    lessonId: index + 1,
    category: row[0],
    words: [
      { bangla: row[1], phonetic: row[2], meaning: row[3] },
      { bangla: row[4], phonetic: row[5], meaning: row[6] }
    ]
  }));

const packByLessonId = new Map(
  supportingVocabularyPacks.map(pack => [pack.lessonId, pack])
);

export const vocabularyReviewOffsets = [1, 3, 7, 15, 30, 60] as const;

export function vocabularyPlanForLesson(lessonId: number) {
  const newPack = packByLessonId.get(lessonId);
  if (!newPack) {
    throw new Error(`No supporting vocabulary is authored for lesson ${lessonId}.`);
  }

  const reviewPacks = vocabularyReviewOffsets
    .map(offset => packByLessonId.get(lessonId - offset))
    .filter((pack): pack is SupportingVocabularyPack => Boolean(pack));

  return { newPack, reviewPacks };
}
